<script setup lang="ts">
import { onMounted, ref } from 'vue';
import { useRouter } from 'vue-router';
import MxIcon from '@/components/MxIcon.vue';
import MxButton from '@/components/MxButton.vue';
import { markOnboarded } from '@/composables/useOnboarding';

const router = useRouter();
const slidesEl = ref<HTMLDivElement | null>(null);
const activeIdx = ref(0);

interface Slide {
  id: string;
  title: string;
  hint: string;
  variant: 'icon-sparkles' | 'collage' | 'icon-wand';
}

const SLIDES: Slide[] = [
  {
    id: 'whatami',
    title: 'AI-видео из вашего фото',
    hint: 'Один кадр — десятки секунд видео в любом стиле.',
    variant: 'icon-sparkles',
  },
  {
    id: 'templates',
    title: 'Шаблоны на любой вкус',
    hint: 'Стартуйте с готовых пресетов: танцы, липсинк, коллажи.',
    variant: 'collage',
  },
  {
    id: 'go',
    title: 'Готово к старту',
    hint: 'Загрузите фото, выберите шаблон — и поехали.',
    variant: 'icon-wand',
  },
];

const COLLAGE_POSTERS = [
  '/templates/korean-dance/poster.jpg',
  '/templates/take-phone/poster.jpg',
  '/templates/want-to-say/poster.jpg',
];

const onScroll = () => {
  if (!slidesEl.value) return;
  const w = slidesEl.value.clientWidth;
  if (w === 0) return;
  activeIdx.value = Math.round(slidesEl.value.scrollLeft / w);
};

function goTo(idx: number) {
  const el = slidesEl.value;
  if (!el) return;
  const w = el.clientWidth;
  if (typeof el.scrollTo === 'function') {
    el.scrollTo({ left: idx * w, behavior: 'smooth' });
  } else {
    el.scrollLeft = idx * w;
  }
}

function next() {
  if (activeIdx.value < SLIDES.length - 1) {
    goTo(activeIdx.value + 1);
  } else {
    finish();
  }
}

function finish() {
  markOnboarded();
  void router.replace('/');
}

const isLast = () => activeIdx.value === SLIDES.length - 1;

onMounted(() => {
  const el = slidesEl.value;
  if (!el) return;
  // На случай первого рендера: индекс 0 уже верный, но scroll-position
  // на iOS иногда «прыгает» — пинаем явный скролл в начало.
  if (typeof el.scrollTo === 'function') {
    el.scrollTo({ left: 0, behavior: 'auto' });
  } else {
    el.scrollLeft = 0;
  }
});
</script>

<template>
  <section class="ob">
    <button v-if="!isLast()" class="ob__skip" data-test="ob-skip" @click="finish">
      Пропустить
    </button>

    <div ref="slidesEl" class="ob__track" data-test="ob-track" @scroll="onScroll">
      <article v-for="s in SLIDES" :key="s.id" class="ob__slide" :data-slide-id="s.id">
        <div class="ob__hero">
          <template v-if="s.variant === 'icon-sparkles'">
            <div class="ob__icon-wrap"><MxIcon name="Sparkles" :size="72" /></div>
          </template>
          <template v-else-if="s.variant === 'icon-wand'">
            <div class="ob__icon-wrap"><MxIcon name="Wand2" :size="72" /></div>
          </template>
          <template v-else>
            <div class="ob__collage">
              <img
                v-for="src in COLLAGE_POSTERS"
                :key="src"
                :src="src"
                alt=""
                class="ob__collage-img"
                loading="eager"
              />
            </div>
          </template>
        </div>
        <h1 class="ob__title">{{ s.title }}</h1>
        <p class="ob__hint">{{ s.hint }}</p>
      </article>
    </div>

    <div class="ob__dots" aria-hidden="true">
      <span v-for="(_, i) in SLIDES" :key="i" class="ob__dot" />
      <span
        class="ob__dot-pill"
        :style="{ transform: `translateX(${activeIdx * 14}px)` }"
      />
    </div>

    <div class="ob__cta">
      <MxButton variant="primary" data-test="ob-next" @click="next">
        {{ isLast() ? 'Поехали' : 'Далее' }}
      </MxButton>
    </div>
  </section>
</template>

<style scoped>
.ob {
  position: fixed;
  inset: 0;
  display: grid;
  grid-template-rows: 1fr auto auto;
  background: var(--bg-base);
  z-index: 60;
  overflow: hidden;
}
.ob__skip {
  position: absolute;
  top: calc(env(safe-area-inset-top) + var(--sp-3));
  right: var(--sp-4);
  z-index: 2;
  padding: 8px 14px;
  border-radius: 999px;
  background: var(--bg-elevated);
  color: var(--text-secondary);
  font-size: var(--fs-sm);
  font-weight: 500;
  -webkit-tap-highlight-color: transparent;
  transition: transform 0.12s ease;
}
.ob__skip:active { transform: scale(0.96); }
.ob__track {
  display: flex;
  overflow-x: auto;
  scroll-snap-type: x mandatory;
  scrollbar-width: none;
  -webkit-overflow-scrolling: touch;
  width: 100%;
  height: 100%;
}
.ob__track::-webkit-scrollbar { display: none; }
.ob__slide {
  flex: 0 0 100%;
  scroll-snap-align: start;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  padding: var(--sp-6) var(--sp-5);
  text-align: center;
  gap: var(--sp-4);
}
.ob__hero {
  display: grid;
  place-items: center;
  width: 100%;
  min-height: 220px;
  margin-bottom: var(--sp-3);
}
.ob__icon-wrap {
  width: 144px;
  height: 144px;
  border-radius: 50%;
  display: grid;
  place-items: center;
  background:
    radial-gradient(120% 120% at 30% 0%, var(--accent-soft) 0%, transparent 70%),
    var(--bg-elevated);
  color: var(--accent);
}
.ob__collage {
  display: grid;
  grid-template-columns: 1fr 1.4fr 1fr;
  gap: var(--sp-2);
  width: 100%;
  max-width: 320px;
  align-items: center;
}
.ob__collage-img {
  width: 100%;
  aspect-ratio: 9 / 16;
  object-fit: cover;
  border-radius: var(--r-md);
  border: 1px solid var(--hairline);
}
.ob__collage-img:nth-child(2) {
  aspect-ratio: 9 / 14;
  transform: scale(1.08);
  border-color: var(--accent-soft);
}
.ob__title {
  font-family: var(--font-display);
  font-size: var(--fs-2xl);
  font-weight: 700;
  letter-spacing: -0.02em;
  line-height: 1.1;
  max-width: 18ch;
}
.ob__hint {
  color: var(--text-secondary);
  font-size: var(--fs-base);
  line-height: 1.4;
  max-width: 32ch;
}
.ob__dots {
  position: relative;
  display: flex;
  justify-content: center;
  gap: 8px;
  padding: var(--sp-3) 0;
}
.ob__dot {
  width: 6px;
  height: 6px;
  border-radius: 50%;
  background: var(--hairline);
}
/* Single sliding accent pill on top of the 3 dots. dot(6) + gap(8) = 14px шаг.
   Pill 22×6, центр совмещён с центром dot[0]: left = центр_контейнера - (3*6 + 2*8)/2 - (22-6)/2 = 50% - 17 - 8 = 50% - 25.
   Translate transform — hardware-accelerated, не дёргает layout. */
.ob__dot-pill {
  position: absolute;
  top: var(--sp-3);
  left: calc(50% - 25px);
  width: 22px;
  height: 6px;
  border-radius: 999px;
  background: var(--accent);
  transition: transform 0.22s cubic-bezier(0.16, 1, 0.3, 1);
  will-change: transform;
}
.ob__cta {
  padding: 0 var(--sp-4) calc(var(--sp-5) + env(safe-area-inset-bottom));
}
.ob__cta .btn { width: 100%; }
</style>
